import type { SvgIconComponent } from "@mui/icons-material";
import getUser, { isTester } from "src/models/User";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import BallotOutlinedIcon from "@mui/icons-material/BallotOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

export type MenuItem = {
	id: string;
	title: string;
	link: string;
	Icon: SvgIconComponent;
};

export type MenuGroup = {
	id: string;
	title: string;
	items: MenuItem[];
};

const TESTER_HIDDEN_IDS = new Set([
	"home",
	"programs",
	"testimonials",
	"news",
	"jobs",
]);

const RESTRICTED_USER_IDS = new Set([20061, 27749]);
const RESTRICTED_HIDDEN_IDS = new Set([
	"home",
	"programs",
	"testimonials",
	"news",
	"e-courses",
]);

const classItems: MenuItem[] = [
	{ id: "overview", title: "Overview", link: "/dashboards", Icon: DesignServicesOutlinedIcon },
	{ id: "e-courses", title: "E-Courses", link: "/classes/e-course", Icon: BallotOutlinedIcon },
	{ id: "tryout", title: "Try out", link: "/classes/tryout", Icon: CalendarMonthOutlinedIcon },
	{ id: "classes", title: "Classes", link: "/classes/classroom", Icon: SchoolOutlinedIcon },
];

const otherItems: MenuItem[] = [
	{ id: "tickets", title: "Tickets", link: "/others/reports", Icon: ConfirmationNumberOutlinedIcon },
	{ id: "transactions", title: "Transactions", link: "/others/transactions", Icon: ShoppingCartOutlinedIcon },
	{ id: "application-survey", title: "Application Survey", link: "/others/application-survey", Icon: AssignmentOutlinedIcon },
	{ id: "account", title: "Account", link: "/others/account", Icon: AccountCircleOutlinedIcon },
];

const exploreItems: MenuItem[] = [
	{ id: "home", title: "Home", link: "/", Icon: WidgetsOutlinedIcon },
	{ id: "programs", title: "Programs", link: "/programs", Icon: StorefrontOutlinedIcon },
	{ id: "testimonials", title: "Testimonies", link: "/testimony", Icon: ThumbUpOutlinedIcon },
	{ id: "news", title: "News", link: "/blogs", Icon: ArticleOutlinedIcon },
	{ id: "jobs", title: "Jobs", link: "/jobs", Icon: WorkOutlineIcon },
];

// Tes Love Language selalu paling bawah
const toolItems: MenuItem[] = [
	{ id: "cv-builder", title: "CV Builder", link: "/cv-builder", Icon: DescriptionOutlinedIcon },
	{ id: "skd-readiness", title: "SKD Readiness", link: "/skd-readiness", Icon: FactCheckOutlinedIcon },
	{ id: "interview-readiness", title: "Interview Readiness", link: "/interview-readiness", Icon: RecordVoiceOverOutlinedIcon },
	{ id: "tes-kepribadian", title: "Tes Kepribadian", link: "/tes-kepribadian", Icon: PsychologyOutlinedIcon },
	{ id: "tes-love-language", title: "Tes Love Language", link: "/tes-love-language", Icon: FavoriteBorderOutlinedIcon },
];

export const CALENDAR_LINK = "/calendar?isMobile=true";

const calendarItem: MenuItem = {
	id: "calendar",
	title: "Calendar",
	link: CALENDAR_LINK,
	Icon: EventOutlinedIcon,
};

function isItemVisible(id: string, tester: boolean, userId?: number) {
	if (tester && TESTER_HIDDEN_IDS.has(id)) return false;
	if (userId != null && RESTRICTED_USER_IDS.has(userId) && RESTRICTED_HIDDEN_IDS.has(id)) {
		return false;
	}
	return true;
}

/** Grup menu siswa yang dipakai halaman All Menu dan sidebar dashboard. */
export function getMenuGroups(showCalendar = false): MenuGroup[] {
	const tester = isTester();
	const userId = getUser()?.id as number | undefined;
	const visible = (item: MenuItem) => isItemVisible(item.id, tester, userId);
	const learning = classItems.filter(visible);
	if (showCalendar) learning.push(calendarItem);

	return [
		{ id: "class", title: "Class", items: learning },
		{ id: "others", title: "Others", items: otherItems.filter(visible) },
		{ id: "explore", title: "Explore", items: exploreItems.filter(visible) },
		{ id: "tools", title: "Tools", items: toolItems.filter(visible) },
	].filter((section) => section.items.length > 0);
}

/** `/` hanya cocok persis, link lain juga cocok untuk subhalamannya. */
export function isMenuLinkActive(link: string, pathname: string) {
	const path = link.split("?")[0];
	if (path === "/") return pathname === "/";
	return pathname === path || pathname.startsWith(`${path}/`);
}
